/**
 * Firebase utility functions
 *
 * Helper functions for working with Firebase Realtime Database
 */

/**
 * Remove undefined values from an object before writing to Firebase
 *
 * Firebase Realtime Database explicitly rejects writes containing undefined values.
 * It requires either null or property omission. This function filters out all
 * undefined properties from an object to make it safe for Firebase writes.
 *
 * @param data - Object that may contain undefined values
 * @returns New object with undefined values removed
 *
 * @example
 * ```typescript
 * interface Story {
 *   title: string;
 *   content?: string;  // Could be undefined
 *   photos?: string[]; // Could be undefined
 * }
 *
 * const story: Story = {
 *   title: "My Story"
 *   // content and photos are undefined
 * };
 *
 * await setData("/stories/123", {
 *   ...cleanForFirebase(story),  // Only includes title
 *   dateCreated: Date.now()
 * });
 * ```
 */
export function cleanForFirebase<T extends Record<string, any>>(data: T): Partial<T> {
  return Object.entries(data).reduce((acc, [key, value]) => {
    if (value !== undefined) {
      acc[key as keyof T] = value;
    }
    return acc;
  }, {} as Partial<T>);
}

/**
 * Deep clean an object for Firebase, recursively removing undefined values
 *
 * Use this for nested objects that may contain undefined values at any level.
 *
 * @param data - Object that may contain nested undefined values
 * @returns New object with all undefined values removed at all levels
 */
export function deepCleanForFirebase<T extends Record<string, any>>(data: T): Partial<T> {
  return Object.entries(data).reduce((acc, [key, value]) => {
    if (value === undefined) {
      // Skip undefined values
      return acc;
    }

    if (value !== null && typeof value === 'object' && !Array.isArray(value)) {
      // Recursively clean nested objects
      const cleaned = deepCleanForFirebase(value);
      if (Object.keys(cleaned).length > 0) {
        acc[key as keyof T] = cleaned as any;
      }
    } else {
      // Keep primitives, null, and arrays
      acc[key as keyof T] = value;
    }

    return acc;
  }, {} as Partial<T>);
}
